import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import OTM from 'js/constants/otm';
import SearchFilterEncoder from 'js/helpers/search/SearchFilterEncoder';
import RapidSearchConstant from 'js/constants/rapidSearch';
import Commons from 'js/helpers/Commons';
import ListPropertyPreview from 'js/constants/listPropertyPreview';

const DEFAULT_SORT = {
    [ListPropertyPreview.SEARCH_TYPE.ADDRESS]: RapidSearchConstant.SEARCH_DEFAULT_SORT.ADDRESS,
    [ListPropertyPreview.SEARCH_TYPE.RADIUS]: RapidSearchConstant.SEARCH_DEFAULT_SORT.RADIUS,
};

const isDefaultDate = (key, filters, statusType, filtersFromUrl) => {
    if (!key.toLowerCase().includes('date')) return false;

    const {
        min: currentMin, max: currentMax,
    } = filters.getFlatFilters(filtersFromUrl, statusType)?.[key] || {};
    const { min: defaultMin, max: defaultMax } = filters?.defaultValue?.[statusType]?.[key] || {};

    return currentMin === defaultMin && currentMax === defaultMax;
};

const isDefaultTenure = (key, filters, statusType, filtersFromUrl) => {
    if (key !== 'tenureData') return false;

    const current = filters.getFlatFilters(filtersFromUrl, statusType)[key] || {};
    const {
        holdPeriodHouse: defaultHouse,
        holdPeriodUnit: defaultUnit,
    } = filters.defaultValue[statusType] || {};

    return Commons.isEqual(current?.holdPeriodHouse, defaultHouse) && Commons.isEqual(current?.holdPeriodUnit, defaultUnit);
};

const isDefaultSort = (key, filters, searchType) => {
    const defaultSort = DEFAULT_SORT[searchType] || DEFAULT_SORT.address;
    return key === 'sort'
    && Commons.isEqual(filters.sort, defaultSort);
}
const isDefaultDaysOnMarket = (key, filters, statusType, filtersFromUrl) => {
    if (key !== 'salesLastCampaignDaysOnMarket') return false;
    const current = filters.getFlatFilters(filtersFromUrl, statusType)[key];
    const defaultValue = filters.defaultValue[statusType][key];

    return current === defaultValue;
}

const sortByPriority = (entries, priorityKeys) =>
    entries.sort(([keyA], [keyB]) => {
        const indexA = priorityKeys.indexOf(keyA);
        const indexB = priorityKeys.indexOf(keyB);

        if (indexA !== -1 && indexB !== -1) return indexA - indexB; // Both keys in priority list
        if (indexA !== -1) return -1; // Only keyA is in priority list
        if (indexB !== -1) return 1;  // Only keyB is in priority list

        return keyA.localeCompare(keyB); // Alphabetical sort for others
    });
const sortSearchParams = (searchParams, priorityKeys) =>
    new URLSearchParams(sortByPriority([...searchParams.entries()], priorityKeys));
const useSearchUrlGenerator = (props) => {
    const location = useLocation();
    const history = useHistory();

    // Prevents URL from updating.
    // Necessary for search types that is personalized

    const generateNewSearchParams = () => {
        const searchParams = new URLSearchParams(location.search);
        const newSearchParams = new URLSearchParams();

        // Adding base filters back to URL because actual filters does not hold these
        RapidSearchConstant.BASE_FILTER_KEYS?.[props.searchType]?.forEach((key) => {
            if (searchParams.has(key)) {
                newSearchParams.set(key, searchParams.get(key));
            }
        });

        return newSearchParams;
    };

    const handleSpecialSuburbCase = (filters, searchParams) => {
        const suburbOnly = filters?.addressPostcode
        || filters?.addressPostcodeState
        || filters?.addressTown
        || filters?.addressSuburb
        if (suburbOnly) {
            searchParams.set(suburbOnly, 'true');
        }
    }

    return (incomingFilters) => {
        if (!props.shouldUpdate) return;

        const cloneFilters = JSON.parse(JSON.stringify(incomingFilters));

        // Removing default filters for shorter URL
        if (incomingFilters.statusType === OTM.ALL) delete cloneFilters.statusType;
        if (incomingFilters.active === true) delete cloneFilters.active;

        const searchParams = generateNewSearchParams();

        Object.keys(cloneFilters).forEach((key) => {
            const formatter = SearchFilterEncoder[key];
            const _isDefaultDate = isDefaultDate(
                key,
                props.filters,
                incomingFilters.statusType,
                props.filtersFromUrl,
            );
            const _isDefaultTenure = isDefaultTenure(key,
                props.filters,
                incomingFilters.statusType,
                props.filtersFromUrl,
            );
            const _isDefaultSort = isDefaultSort(key, cloneFilters, props.searchType);
            const _isDefaultDaysOnMarket = isDefaultDaysOnMarket(
                key,
                props.filters,
                incomingFilters.statusType,
                props.filtersFromUrl,
            );

            if (formatter
                && !_isDefaultDate
                && !_isDefaultTenure
                && !_isDefaultSort
                && !_isDefaultDaysOnMarket) {
                const formatted = formatter(cloneFilters[key], cloneFilters);
                if (formatted) {
                    searchParams.set(key, formatted);
                }
            }
        })

        handleSpecialSuburbCase();

        history.replace({
            // Keep the current path
            pathname: location.pathname,
            // Add the updated and sorted query parameters
            search: sortSearchParams(searchParams, RapidSearchConstant.BASE_FILTER_KEYS[props.searchType]).toString(),
            // Keep the current state. This is needed for the mixpanel
            // events to determine if user uses direct link or from searchbar when doing a search
            state: {
                entryPoint: location.state?.entryPoint,
                mapSearch: !!location.state?.mapSearch,
            },
        });
    };
}

useSearchUrlGenerator.propTypes = {
    searchType: PropTypes.string,
    filters: PropTypes.object,
    shouldUpdate: PropTypes.bool,
};

export default useSearchUrlGenerator;
